import { INumberFormatter, NUMBER_FORMATTER_TOKEN } from '@cgs/common';
import { LAUNCH_PARAMS_STORE_TOKEN, LaunchParamsStore } from '@cgs/machines';
import { TrifectaGameRules } from '@cgs/provider-trifecta';
import { Compatibility, Container, TextSceneObject, TtfTextSceneObject, Vector2 } from '@cgs/wge';

export class SantaGameRules extends TrifectaGameRules {
  private readonly launchParamsStore: LaunchParamsStore;
  private readonly numberFormatter: INumberFormatter;

  constructor(container: Container) {
    super(container);

    this.launchParamsStore = container.resolve<LaunchParamsStore>(LAUNCH_PARAMS_STORE_TOKEN);
    this.numberFormatter = container.resolve<INumberFormatter>(NUMBER_FORMATTER_TOKEN);
  }

  protected getMarkerTextId(): string {
    return 'price_3_5';
  }

  public show(): void {
    this.rerenderPayTable();
    super.show();
  }

  private rerenderPayTable(): void {
    const symbols = this.launchParamsStore.state.symbols.filter((x) =>
      (x.gains || []).some((gain) => gain)
    );

    const markerText = this.scrollableProgressBar.scene.findById<TtfTextSceneObject>(
      this.getMarkerTextId()
    );

    const startFontSize = markerText?.forceFontSize ?? markerText?.fontSize ?? 17;

    let minFontSize = startFontSize;

    symbols.forEach((symbol) => {
      symbol.gains!.forEach((gain, index) => {
        if (!gain) {
          return;
        }
        const symbolText = this.scrollableProgressBar.scene.findById<TtfTextSceneObject>(
          `price_${symbol.id}_${index + 1}`
        );
        if (!symbolText) {
          return;
        }

        symbolText.text = `${index + 1}x ${this.currency} ${this.numberFormatter.format(
          gain * this.slotSession.currentBet.bet,
          this.currencyDecimals
        )}`;

        const fontCoefficient = 13.5 / symbolText.text.length;

        minFontSize = Math.min(minFontSize, startFontSize * fontCoefficient);
      });
    });

    symbols.forEach((symbol) => {
      symbol.gains!.forEach((gain, index) => {
        if (!gain) {
          return;
        }
        const symbolText = this.scrollableProgressBar.scene.findById<TtfTextSceneObject>(
          `price_${symbol.id}_${index + 1}`
        );
        if (!symbolText) {
          return;
        }
        symbolText.fontSize = minFontSize;
      });
    });
  }

  protected afterInitGameRules(): void {
    this.renderGameSpecifications();
    this.adoptGapLines();
    // this.alignDotsInBonusSection();
  }

  private renderGameSpecifications(): void {
    const gameRTP = this.scrollableProgressBar.scene.findById<TtfTextSceneObject>(
      'gameSpecificationsDescriptionLbl_0'
    );
    if (gameRTP) {
      gameRTP.text = gameRTP.text.replace('XX', `${this.launchParams.rtp}%`);
    }

    const buyBonusRTP = this.scrollableProgressBar.scene.findById<TtfTextSceneObject>(
      'buyFeatureDescriptionLbl_1'
    );
    if (buyBonusRTP) {
      buyBonusRTP.text = buyBonusRTP.text.replace('XX', `${this.launchParams.buyBonusRtp}%`);
    }

    const hitRateText = this.scrollableProgressBar.scene.findById<TtfTextSceneObject>(
      'gameSpecificationsDescriptionLbl_1'
    );
    if (!hitRateText) {
      throw new Error('buy bonus RTP not found');
    }
    hitRateText.text = hitRateText.text.replace('XX', `${this.launchParams.hitRate}%`);

    const volatilityText = this.scrollableProgressBar.scene.findById<TtfTextSceneObject>(
      'gameSpecificationsDescriptionLbl_2'
    );
    if (!volatilityText) {
      throw new Error('volatility not found');
    }
    volatilityText.text = volatilityText.text.replace('XX', this.launchParams.volatility);
  }

  private clearIntroduction(): void {
    const labels = new Array(3).fill(0).map((_, index) => index + 2);

    labels.forEach((label) => {
      const introductionDescriptionLbl =
        this.scrollableProgressBar.scene.findById<TtfTextSceneObject>(
          `introductionDescriptionLbl_${label}`
        );
      this.hideLineAndUpOthers(introductionDescriptionLbl);
    });
  }

  private clearSymbolsAndPayouts(): void {
    const symbolsAndPayoutsDescriptionLbl_3 =
      this.scrollableProgressBar.scene.findById<TtfTextSceneObject>(
        'symbolsAndPayoutsDescriptionLbl_3'
      );
    this.hideLineAndUpOthers(symbolsAndPayoutsDescriptionLbl_3);
  }

  private clearBuyFeature(): void {
    const buyFeatureDescriptionLbl_4 =
      this.scrollableProgressBar.scene.findById<TtfTextSceneObject>('buyFeatureDescriptionLbl_4');
    this.hideLineAndUpOthers(buyFeatureDescriptionLbl_4);
  }

  private clearSpecialSymbols(): void {
    const specialSymbolsDescriptionLbl_1 =
      this.scrollableProgressBar.scene.findById<TtfTextSceneObject>('specialSymbolDescriptionLbl');
    this.hideLineAndUpOthers(specialSymbolsDescriptionLbl_1);

    const specialSymbolsTitleLbl =
      this.scrollableProgressBar.scene.findById<TtfTextSceneObject>('specialSymbolsTitleLbl');
    if (!specialSymbolsTitleLbl) {
      return;
    }
    this.upOthers(specialSymbolsTitleLbl, 50);
  }

  private increaseMobileFontSize(): void {
    const markerText = this.scrollableProgressBar.scene.findById<TextSceneObject>(
      this.getMarkerTextId()
    );
    if (!markerText) {
      throw new Error('marker text not found');
    }

    markerText.parent!.childs.forEach((child) => {
      if (child instanceof TtfTextSceneObject) {
        const fontSize = this.localizationManager.getFontSize(child.id) ?? child.fontSize;

        child.forceFontSize = fontSize + 5;
      }
    });
  }

  private removeBuyFeatureDescription(): void {
    const buyFeatureDescription =
      this.scrollableProgressBar.scene.findById<TtfTextSceneObject>('buyFeatureTitleLbl');

    const line1 = this.scrollableProgressBar.scene.findById<TtfTextSceneObject>(
      'buyFeatureDescriptionLbl_0'
    );
    const line2 = this.scrollableProgressBar.scene.findById<TtfTextSceneObject>(
      'buyFeatureDescriptionLbl_1'
    );
    const line3 = this.scrollableProgressBar.scene.findById<TtfTextSceneObject>(
      'buyFeatureDescriptionLbl_2'
    );
    const line4 = this.scrollableProgressBar.scene.findById<TtfTextSceneObject>(
      'buyFeatureDescriptionLbl_3'
    );
    const line5 = this.scrollableProgressBar.scene.findById<TtfTextSceneObject>(
      'buyFeatureDescriptionLbl_4'
    );

    this.hideLineAndUpOthers(buyFeatureDescription);
    this.hideLineAndUpOthers(line1);
    this.hideLineAndUpOthers(line2);
    this.hideLineAndUpOthers(line3);
    this.hideLineAndUpOthers(line4);
    this.hideLineAndUpOthers(line5);
  }

  private removeRTPDescription(): void {
    const rtpDescription = this.scrollableProgressBar.scene.findById<TtfTextSceneObject>(
      'gameSpecificationsDescriptionLbl_0'
    )!;

    const buyFeatureRtp = this.scrollableProgressBar.scene.findById<TtfTextSceneObject>(
      'buyFeatureDescriptionLbl_1'
    );

    this.hideLineAndUpOthers(rtpDescription);
    this.hideLineAndUpOthers(buyFeatureRtp);
  }

  private removeMaxWinDescription(): void {
    const maxWinDescription = this.scrollableProgressBar.scene.findById<TtfTextSceneObject>(
      'gameSpecificationsDescriptionLbl_3'
    );
    const maxWinDescription2 = this.scrollableProgressBar.scene.findById<TtfTextSceneObject>(
      'generalRulesDescriptionLbl_4'
    );
    const maxWinDescription3 = this.scrollableProgressBar.scene.findById<TtfTextSceneObject>(
      'symbolsAndPayoutsDescriptionLbl_2'
    );
    const maxWinDescription4 = this.scrollableProgressBar.scene.findById<TtfTextSceneObject>(
      'freeSpinsBonusRoundDescriptionLbl_5'
    );

    this.hideLineAndUpOthers(maxWinDescription);
    this.hideLineAndUpOthers(maxWinDescription2);
    this.hideLineAndUpOthers(maxWinDescription3);
    this.hideLineAndUpOthers(maxWinDescription4);
  }

  protected beforeInitGameRules(): void {
    if (Compatibility.IsMobileBrowser) {
      this.increaseMobileFontSize();
    }
    this.clearIntroduction();
    this.clearSymbolsAndPayouts();
    this.clearBuyFeature();
    this.clearSpecialSymbols();

    if (!this.launchParams.showBuyFeatures) {
      this.removeBuyFeatureDescription();
    }

    if (!this.launchParams.showRtp) {
      this.removeRTPDescription();
    }

    if (!this.launchParams.showMaxWin) {
      this.removeMaxWinDescription();
    }
  }

  protected hideLineAndUpOthers(textLine: TtfTextSceneObject | null): void {
    if (!textLine) {
      return;
    }

    const size = textLine.size;

    this.upOthers(textLine, size.y);

    textLine.parent!.removeChild(textLine);
  }

  protected upOthers(textLine: TtfTextSceneObject, delta: number): void {
    textLine
      .parent!.childs.filter((child) => child.position.y > textLine.position.y)
      .forEach((child) => {
        child.position = child.position.clone().subtract(new Vector2(0, delta));
      });
  }

  protected adoptGapLines() {
    const lines = this.scrollableProgressBar.scene.childs[0].childs[0].childs
      .filter((child) => child instanceof TtfTextSceneObject)
      .sort((a, b) => a.position.y - b.position.y);

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      const previousLine = lines[i - 1];

      const lineId = line.id.substring(0, 5);
      const previousLineId = previousLine.id.substring(0, 5);

      if (lineId !== previousLineId) {
        continue;
      }

      const gap = line.position.y - (previousLine.position.y + previousLine.size.y);

      if (gap > 0 && gap < 100) {
        line.position = line.position.clone().subtract(new Vector2(0, gap - 2));
      }
    }
  }
}
